import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
  downloadFullDuplexInstruction,
  getFullDuplexFeedback,
  saveFullDuplexFeedback,
  updateFullDuplexTestState,
} from '../../api/fullDuplexFeedback';
import { useSearchParams } from 'react-router-dom';
import { loadLogbuchPdfBrandingAssets } from '../../components/pdf/logbuchPdfBuilder';
import {
  buildFullDuplexFeedbackPdfDocument,
  buildFullDuplexFeedbackPdfFilename,
} from '../../components/pdf/fullDuplexFeedbackPdfBuilder';
import FullDuplexResults from './FullDuplexResults';
import FullDuplexScoreField from './FullDuplexScoreField';
import {
  buildFullDuplexFormUrl,
  buildIndividualAnalysis,
  buildInitialFullDuplexValues,
  hasAnyFullDuplexScore,
} from './fullDuplexModel';

pdfMake.addVirtualFileSystem(pdfFonts);

const INFO_PARAGRAPHS = [
  'Wir haben von der Firma Comhead erneut Geräte zum Testen zur Verfügung gestellt bekommen. Ziel ist es ein Vollduplexfunksystem (kommunizieren via Art Telefonkonferenz mit geringer Latenz) zu erhalten, welches bei Bedarf auch auf ein Push-To-Talk System umgestellt werden kann.',
  'Zum aktuellen Zeitpunkt ist es geplant, dass zukünftig das Vollduplexsystem und der Digitalfunk mitgeführt wird, weshalb bei den Versuchsgeräten ein Kombinationsgerät beiliegt, welches die Bedienung und Empfang beider Geräte über eine Bedienstelle ermöglicht. Bei dem Kombinationsgerät handelt es sich um einen Prototyp. Das Serienmodell ist komfortabler ausgestattet.',
  'Bitte testet das Funkgerät mit den 3 Headsets und gebt Eure Meinung zu den einzelnen Punkten ab. Es gilt das Schulnotenprinzip.',
  'Testet die Geräte bitte unbedingt in unterschiedlich lauter Umgebung (Stille, Musik, Drehleitermotorengeräusch) und auch bei körperlicher Arbeit im Seil.',
  'Über eine Freitexteintragung im Feld „Bemerkungen“ würden wir uns besonders freuen.',
  'Meldet Euch bei Rückfragen bitte direkt bei Felix.',
  'Viel Spaß beim ausprobieren!',
];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('de-DE');
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

function SubmissionDetails({ config, submission }) {
  if (!submission) return null;
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Stand: {formatDateTime(submission.updatedAt || submission.createdAt)}
      </Typography>
      {(config?.deviceGroups || []).map((group) => (
        <Box key={group.key}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75 }}>{group.label}</Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {group.criteria.map((criterion) => (
                  <TableRow key={criterion.key}>
                    <TableCell>{criterion.label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, width: 130 }}>
                      {submission.values?.[criterion.key] == null ? 'Nicht getestet' : `Note ${submission.values[criterion.key]}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Bemerkungen</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{submission.remarks || 'Keine Bemerkung'}</Typography>
      </Box>
    </Stack>
  );
}

export default function FullDuplexFeedbackPage() {
  const [searchParams] = useSearchParams();
  const formAnchorRef = useRef(null);
  const [expanded, setExpanded] = useState({
    explanation: true,
    form: true,
    results: false,
  });
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailSubmission, setDetailSubmission] = useState(null);
  const [resultMode, setResultMode] = useState('group');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState('');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrMessage, setQrMessage] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [stateDialog, setStateDialog] = useState({ open: false, status: 'closed', reason: '' });
  const [stateBusy, setStateBusy] = useState(false);

  const loadData = async ({ preserveDraft = false } = {}) => {
    const response = await getFullDuplexFeedback();
    const nextData = response.data;
    setData(nextData);
    if (!preserveDraft) {
      setValues(buildInitialFullDuplexValues(nextData.config, nextData.ownSubmission?.values));
      setRemarks(nextData.ownSubmission?.remarks || '');
    }
    return nextData;
  };

  useEffect(() => {
    loadData().catch((loadError) => {
      setError(loadError.response?.data?.error || 'Vollduplex-Testbogen konnte nicht geladen werden.');
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!data || searchParams.get('section') !== 'form') return;
    setExpanded((current) => ({ ...current, form: true }));
    window.setTimeout(() => formAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [data, searchParams]);

  const selectedSubmission = useMemo(
    () => (data?.submissions || []).find((submission) => Number(submission.id) === Number(selectedSubmissionId)) || null,
    [data?.submissions, selectedSubmissionId],
  );
  const displayedAnalysis = resultMode === 'individual' && selectedSubmission
    ? buildIndividualAnalysis(data?.config, selectedSubmission)
    : data?.analysis;
  const isClosed = data?.state?.status === 'closed';
  const canSave = !isClosed && hasAnyFullDuplexScore(values);
  const qrUrl = typeof window === 'undefined'
    ? ''
    : buildFullDuplexFormUrl(window.location.origin);

  const toggleAccordion = (key) => (_, isExpanded) => {
    setExpanded((current) => ({ ...current, [key]: isExpanded }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await saveFullDuplexFeedback({ values, remarks });
      await loadData();
      setExpanded((current) => ({ ...current, results: true }));
      setSuccess('Bewertungsbogen gespeichert.');
    } catch (saveError) {
      setError(saveError.response?.data?.error || 'Bewertungsbogen konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleInstructionDownload = async (instruction) => {
    try {
      setError('');
      const response = await downloadFullDuplexInstruction(instruction.key);
      triggerBlobDownload(response.data, `${instruction.label}.pdf`);
    } catch (downloadError) {
      setError(downloadError.response?.data?.error || 'Anleitung konnte nicht heruntergeladen werden.');
    }
  };

  const openQrDialog = async () => {
    try {
      setQrMessage('');
      setQrDataUrl(await QRCode.toDataURL(qrUrl, {
        width: 360,
        margin: 2,
        color: { dark: '#1B3A5C', light: '#FFFFFF' },
      }));
      setQrDialogOpen(true);
    } catch (qrError) {
      setError(qrError.message || 'QR-Code konnte nicht erzeugt werden.');
    }
  };

  const copyQrImage = async () => {
    try {
      if (!window.ClipboardItem || !navigator.clipboard?.write) {
        throw new Error('Dieser Browser unterstützt das Kopieren von Bildern nicht.');
      }
      const blob = await fetch(qrDataUrl).then((response) => response.blob());
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      setQrMessage('QR-Code wurde in die Zwischenablage kopiert.');
    } catch (copyError) {
      setQrMessage(`${copyError.message} Bitte stattdessen den Link kopieren.`);
    }
  };

  const copyQrLink = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setQrMessage('Link wurde in die Zwischenablage kopiert.');
    } catch {
      setQrMessage('Link konnte nicht kopiert werden.');
    }
  };

  const handlePdfExport = async () => {
    try {
      setExportingPdf(true);
      setError('');
      const brandingAssets = await loadLogbuchPdfBrandingAssets();
      const documentDefinition = buildFullDuplexFeedbackPdfDocument({
        config: data.config,
        analysis: data.analysis,
        submissions: data.submissions || [],
        brandingAssets,
        exportDate: new Date(),
      });
      pdfMake.createPdf(documentDefinition).download(buildFullDuplexFeedbackPdfFilename());
    } catch (pdfError) {
      setError(pdfError.message || 'PDF konnte nicht erstellt werden.');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleStateChange = async () => {
    try {
      setStateBusy(true);
      setError('');
      await updateFullDuplexTestState({
        status: stateDialog.status,
        reason: stateDialog.reason,
      });
      await loadData({ preserveDraft: true });
      setStateDialog({ open: false, status: 'closed', reason: '' });
      setSuccess(stateDialog.status === 'closed' ? 'Testbetrieb geschlossen.' : 'Testbetrieb wieder geöffnet.');
    } catch (stateError) {
      setError(stateError.response?.data?.error || 'Teststatus konnte nicht geändert werden.');
    } finally {
      setStateBusy(false);
    }
  };

  if (loading) {
    return <Typography>Lädt Vollduplex-Testbogen...</Typography>;
  }

  if (!data) {
    return <Alert severity="error">{error || 'Vollduplex-Testbogen konnte nicht geladen werden.'}</Alert>;
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Testbetrieb – Vollduplexfunk</Typography>
        <Typography variant="body1" color="text.secondary">
          Bewertung der bereitgestellten Funkgeräte und Headsets
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
        {data.capabilities.canGenerateQrCode && (
          <Button variant="outlined" startIcon={<QrCode2Icon />} onClick={openQrDialog}>
            QR-Code generieren
          </Button>
        )}
        {data.capabilities.canEvaluate && (
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handlePdfExport} disabled={exportingPdf || !data.submissions?.length}>
            {exportingPdf ? 'PDF wird erstellt...' : 'PDF-Export'}
          </Button>
        )}
        {data.capabilities.canManageTest && (
          <Button
            color={isClosed ? 'success' : 'warning'}
            variant="contained"
            onClick={() => setStateDialog({ open: true, status: isClosed ? 'open' : 'closed', reason: '' })}
          >
            {isClosed ? 'Testbetrieb wieder öffnen' : 'Testbetrieb schließen'}
          </Button>
        )}
      </Stack>

      <Accordion expanded={expanded.explanation} onChange={toggleAccordion('explanation')} sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 800 }}>Erläuterung</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            {INFO_PARAGRAPHS.map((paragraph) => <Typography key={paragraph} variant="body2">{paragraph}</Typography>)}
            <Divider />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Anleitungen</Typography>
            {(data.config.instructions || []).map((instruction) => (
              <Button
                key={instruction.key}
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleInstructionDownload(instruction)}
                sx={{ alignSelf: 'flex-start' }}
              >
                {instruction.label}
              </Button>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Box ref={formAnchorRef} sx={{ scrollMarginTop: 88 }}>
        <Accordion expanded={expanded.form} onChange={toggleAccordion('form')} sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 800 }}>Formular</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2.5}>
              {isClosed && <Alert severity="info">Der Testbetrieb ist geschlossen. Dein Bewertungsbogen ist schreibgeschützt.</Alert>}
              <Alert severity="info">
                Schulnotenprinzip: 1 = sehr gut, 6 = ungenügend. Nicht getestete Punkte können entsprechend markiert bleiben.
              </Alert>
              {(data.config.deviceGroups || []).map((group) => (
                <Stack key={group.key} spacing={1.25}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{group.label}</Typography>
                  <Grid container spacing={1.25}>
                    {group.criteria.map((criterion) => (
                      <Grid key={criterion.key} size={{ xs: 12, md: 6 }}>
                        <FullDuplexScoreField
                          criterion={criterion}
                          value={values[criterion.key]}
                          disabled={isClosed}
                          onChange={(value) => setValues((current) => ({ ...current, [criterion.key]: value }))}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              ))}
              <TextField
                label="Bemerkungen"
                value={remarks}
                onChange={(event) => setRemarks(event.target.value)}
                fullWidth
                multiline
                minRows={4}
                disabled={isClosed}
                helperText="Optional"
              />
              <Button variant="contained" onClick={handleSave} disabled={!canSave || saving} sx={{ alignSelf: 'flex-start' }}>
                {saving ? 'Speichert...' : data.ownSubmission ? 'Bewertungsbogen aktualisieren' : 'Bewertungsbogen speichern'}
              </Button>
              {!isClosed && !hasAnyFullDuplexScore(values) && (
                <Typography variant="caption" color="text.secondary">Bitte mindestens ein Kriterium bewerten.</Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Accordion expanded={expanded.results} onChange={toggleAccordion('results')} sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 800 }}>Auswertung</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {!data.ownSubmission && !data.capabilities.canEvaluate ? (
            <Alert severity="info">Die aktuelle Auswertung wird nach deiner ersten Abgabe sichtbar.</Alert>
          ) : (
            <Stack spacing={2.5}>
              {data.capabilities.canEvaluate && (
                <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                  <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                      <ToggleButtonGroup
                        exclusive
                        value={resultMode}
                        onChange={(_, value) => value && setResultMode(value)}
                      >
                        <ToggleButton value="group">Gruppenmittel</ToggleButton>
                        <ToggleButton value="individual">Einzelbewertung</ToggleButton>
                      </ToggleButtonGroup>
                      {resultMode === 'individual' && (
                        <TextField
                          select
                          size="small"
                          label="Bewertungsbogen"
                          value={selectedSubmissionId}
                          onChange={(event) => setSelectedSubmissionId(event.target.value)}
                          sx={{ minWidth: 260 }}
                        >
                          {(data.submissions || []).map((submission) => (
                            <MenuItem key={submission.id} value={String(submission.id)}>{submission.userName}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {resultMode === 'individual' && data.capabilities.canEvaluate && !selectedSubmission ? (
                <Alert severity="info">Bitte einen Bewertungsbogen auswählen.</Alert>
              ) : (
                <FullDuplexResults
                  config={data.config}
                  analysis={displayedAnalysis}
                  submissions={resultMode === 'individual'
                    ? (selectedSubmission ? [selectedSubmission] : [])
                    : (data.capabilities.canEvaluate ? data.submissions : [data.ownSubmission])}
                  showLegend={resultMode === 'group'}
                  onMarkerClick={data.capabilities.canEvaluate ? (submissionId) => {
                    setDetailSubmission((data.submissions || []).find((submission) => Number(submission.id) === Number(submissionId)) || null);
                  } : undefined}
                />
              )}

              {data.capabilities.canEvaluate && (
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Bemerkungen</Typography>
                  <TableContainer component={Card} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Stand</TableCell>
                          <TableCell>Bemerkung</TableCell>
                          <TableCell align="right">Aktion</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data.submissions || []).filter((submission) => submission.remarks).map((submission) => (
                          <TableRow key={submission.id}>
                            <TableCell>{submission.userName}</TableCell>
                            <TableCell>{formatDateTime(submission.updatedAt)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{submission.remarks}</TableCell>
                            <TableCell align="right">
                              <Button size="small" onClick={() => setDetailSubmission(submission)}>Bogen öffnen</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {!(data.submissions || []).some((submission) => submission.remarks) && (
                          <TableRow><TableCell colSpan={4}>Noch keine Bemerkungen vorhanden.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={Boolean(detailSubmission)} onClose={() => setDetailSubmission(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detailSubmission?.userName || 'Bewertungsbogen'}</DialogTitle>
        <DialogContent dividers>
          <SubmissionDetails config={data.config} submission={detailSubmission} />
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailSubmission(null)}>Schließen</Button></DialogActions>
      </Dialog>

      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>QR-Code zum Bewertungsbogen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} alignItems="center">
            {qrDataUrl && <Box component="img" src={qrDataUrl} alt="QR-Code zum Vollduplex-Testbogen" sx={{ width: '100%', maxWidth: 320 }} />}
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{qrUrl}</Typography>
            {qrMessage && <Alert severity={qrMessage.includes('wurde') ? 'success' : 'warning'} sx={{ width: '100%' }}>{qrMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>
          <Button startIcon={<ContentCopyIcon />} onClick={copyQrLink}>Link kopieren</Button>
          <Button variant="contained" startIcon={<QrCode2Icon />} onClick={copyQrImage}>QR-Code kopieren</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stateDialog.open} onClose={() => !stateBusy && setStateDialog({ open: false, status: 'closed', reason: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{stateDialog.status === 'closed' ? 'Testbetrieb schließen' : 'Testbetrieb wieder öffnen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              {stateDialog.status === 'closed'
                ? 'Nach dem Schließen sind keine neuen Eingaben oder Änderungen möglich.'
                : 'Nach dem Öffnen können Bewertungsbögen wieder gespeichert und geändert werden.'}
            </Alert>
            <TextField
              label="Begründung"
              value={stateDialog.reason}
              onChange={(event) => setStateDialog((current) => ({ ...current, reason: event.target.value }))}
              multiline
              minRows={3}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={stateBusy} onClick={() => setStateDialog({ open: false, status: 'closed', reason: '' })}>Abbrechen</Button>
          <Button variant="contained" disabled={stateBusy || !stateDialog.reason.trim()} onClick={handleStateChange}>
            {stateBusy ? 'Speichert...' : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
